import React, { useMemo } from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import {
  FunnelChart,
  Funnel,
  LabelList,
  Tooltip,
  ResponsiveContainer,
  Sankey,
  Layer,
  Rectangle,
} from "recharts";
import { FunnelStyle } from "@/types/breeze/ChartConfiguration";
import { useElementSize } from "@/hooks/useElementSize";

export interface FunnelStagePoint {
  label: string;
  count: number;
}

interface Props {
  stages: FunnelStagePoint[];
  style?: FunnelStyle;
  height?: string | number;
  emptyMessage?: string;
}

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#a4de6c",
  "#d0ed57",
];

type ChartPoint = {
  name: string;
  value: number;
  fill: string;
  fromPrev: number;
  fromFirst: number;
};

export default function FunnelChartView({
  stages,
  style = "vertical",
  height = "100%",
  emptyMessage = "Add at least one stage to see the funnel.",
}: Props) {
  const chartData: ChartPoint[] = useMemo(() => {
    if (!stages || stages.length === 0) return [];
    const first = stages[0]?.count ?? 0;
    return stages.map((stage, i) => {
      const prev = i === 0 ? stage.count : stages[i - 1]?.count ?? 0;
      const fromPrev = prev > 0 ? (stage.count / prev) * 100 : 0;
      const fromFirst = first > 0 ? (stage.count / first) * 100 : 0;
      return {
        name: stage.label,
        value: stage.count,
        fill: COLORS[i % COLORS.length],
        fromPrev,
        fromFirst,
      };
    });
  }, [stages]);

  if (chartData.length === 0) {
    return (
      <Flex align="center" justify="center" style={{ height }}>
        <Text size="2" color="gray">{emptyMessage}</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="2" style={{ height, minHeight: 240 }}>
      <Box style={{ flex: 1, minHeight: 180, position: "relative" }}>
        {style === "sankey" ? (
          <SankeyBody data={chartData} />
        ) : style === "horizontal" ? (
          <HorizontalFunnelSvg data={chartData} />
        ) : (
          <VerticalBody data={chartData} />
        )}
      </Box>
      <Box>
        <Flex direction="column" gap="1">
          {chartData.map((stage, i) => (
            <Flex key={i} justify="between" align="center" gap="2">
              <Flex gap="2" align="center" style={{ minWidth: 0 }}>
                <Box style={{ width: 8, height: 8, backgroundColor: stage.fill, borderRadius: 2, flexShrink: 0 }} />
                <Text size="1" truncate>{stage.name}</Text>
              </Flex>
              <Flex gap="3" align="baseline" style={{ flexShrink: 0 }}>
                <Text size="1" weight="bold">{stage.value.toLocaleString()}</Text>
                {i > 0 && (
                  <Text size="1" color="gray">
                    {stage.fromPrev.toFixed(0)}% · {stage.fromFirst.toFixed(0)}% of first
                  </Text>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Box>
    </Flex>
  );
}

function VerticalBody({ data }: { data: ChartPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <FunnelChart margin={{ top: 8, right: 128, bottom: 8, left: 96 }}>
        <Tooltip
          formatter={(value: unknown) => Number(value).toLocaleString()}
          labelFormatter={() => ""}
        />
        <Funnel dataKey="value" data={data} isAnimationActive>
          <LabelList
            position="right"
            dataKey="name"
            fill="var(--gray-12)"
            stroke="none"
            fontSize={12}
          />
          <LabelList
            position="left"
            dataKey="value"
            fill="var(--gray-12)"
            stroke="none"
            fontSize={12}
            formatter={(v: unknown) => Number(v).toLocaleString()}
          />
        </Funnel>
      </FunnelChart>
    </ResponsiveContainer>
  );
}

function HorizontalFunnelSvg({ data }: { data: ChartPoint[] }) {
  const { size, ref } = useElementSize<HTMLDivElement>();
  const paddingRight = 130;
  const paddingLeft = 8;
  const paddingY = 12;
  const width = Math.max(320, size.width);
  const heightPx = Math.max(180, size.height);
  const drawWidth = Math.max(80, width - paddingRight - paddingLeft);
  const drawHeight = heightPx - paddingY * 2;
  const centerY = paddingY + drawHeight / 2;

  const first = data[0]?.value ?? 0;
  const barSlot = drawWidth / data.length;
  const halfHeights = data.map((d) => (first > 0 ? (d.value / first) * (drawHeight / 2) : 2));

  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} style={{ width: "100%", height: "100%" }}>
      <svg width={width} height={heightPx} style={{ display: "block" }}>
        {data.map((stage, i) => {
          const x0 = paddingLeft + i * barSlot;
          const x1 = paddingLeft + (i + 1) * barSlot;
          const h0 = halfHeights[i];
          const h1 = i + 1 < data.length ? halfHeights[i + 1] : Math.max(2, halfHeights[i] * 0.15);
          const pts = [
            `${x0},${centerY - h0}`,
            `${x1},${centerY - h1}`,
            `${x1},${centerY + h1}`,
            `${x0},${centerY + h0}`,
          ].join(" ");
          return (
            <g key={i}>
              <polygon points={pts} fill={stage.fill} fillOpacity={0.9} />
              <text
                x={(x0 + x1) / 2}
                y={centerY + 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="var(--gray-12)"
              >
                {stage.value.toLocaleString()}
              </text>
              <text
                x={(x0 + x1) / 2}
                y={centerY + h0 + 14}
                textAnchor="middle"
                fontSize={10}
                fill="var(--gray-11)"
              >
                {stage.name}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SankeyBody({ data }: { data: ChartPoint[] }) {
  const nodes: { name: string; fill?: string; actual: number }[] = [];
  const links: { source: number; target: number; value: number }[] = [];

  data.forEach((s) => nodes.push({ name: s.name, fill: s.fill, actual: s.value }));

  const baseline = data[0]?.value ?? 0;
  const zeroPlaceholder = Math.max(baseline * 0.002, 0.5);

  data.forEach((s, i) => {
    if (i === data.length - 1) return;
    const next = data[i + 1];
    const hasPriorFlow = s.value > 0;
    if (next.value > 0) {
      links.push({ source: i, target: i + 1, value: next.value });
    } else if (hasPriorFlow) {
      links.push({ source: i, target: i + 1, value: zeroPlaceholder });
    }
    const dropped = s.value - next.value;
    if (dropped > 0) {
      const lostIndex = nodes.length;
      nodes.push({ name: `Dropped after ${s.name}`, fill: "var(--gray-6)", actual: dropped });
      links.push({ source: i, target: lostIndex, value: dropped });
    }
  });

  if (links.length === 0) {
    return (
      <Flex align="center" justify="center" style={{ height: "100%" }}>
        <Text size="2" color="gray">Need at least two stages with data to draw a sankey.</Text>
      </Flex>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <Sankey
        data={{ nodes, links }}
        nodePadding={40}
        nodeWidth={12}
        linkCurvature={0.5}
        iterations={64}
        link={{ stroke: "var(--accent-9)", strokeOpacity: 0.25 } as any}
        node={<SankeyNode />}
        margin={{ top: 8, right: 160, bottom: 8, left: 8 }}
      >
        <Tooltip content={<SankeyTooltip nodes={nodes} links={links} />} />
      </Sankey>
    </ResponsiveContainer>
  );
}

function SankeyTooltip({
  active,
  payload,
  nodes,
  links,
}: {
  active?: boolean;
  payload?: Array<{ payload?: { payload?: { source?: number | { name?: string }; target?: number | { name?: string }; value?: number }; name?: string; actual?: number; value?: number } }>;
  nodes: { name: string; actual: number }[];
  links: { source: number; target: number; value: number }[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0].payload;
  if (!item) return null;
  const linkInfo = item.payload;
  if (linkInfo && (typeof linkInfo.source === "number" || typeof linkInfo.source === "object")) {
    const sourceIdx = typeof linkInfo.source === "number" ? linkInfo.source : links.find((l) => nodes[l.source]?.name === (linkInfo.source as { name?: string })?.name)?.source;
    const targetIdx = typeof linkInfo.target === "number" ? linkInfo.target : links.find((l) => nodes[l.target]?.name === (linkInfo.target as { name?: string })?.name)?.target;
    if (sourceIdx == null || targetIdx == null) return null;
    const sourceName = nodes[sourceIdx]?.name ?? "";
    const targetName = nodes[targetIdx]?.name ?? "";
    const targetActual = nodes[targetIdx]?.actual ?? 0;
    return (
      <div style={{ background: "var(--color-panel-solid)", border: "1px solid var(--gray-5)", borderRadius: "var(--radius-2)", padding: "6px 10px", fontSize: 12 }}>
        {sourceName} → {targetName}: {Number(targetActual).toLocaleString()}
      </div>
    );
  }
  const name = item.name ?? "";
  const actual = item.actual ?? 0;
  return (
    <div style={{ background: "var(--color-panel-solid)", border: "1px solid var(--gray-5)", borderRadius: "var(--radius-2)", padding: "6px 10px", fontSize: 12 }}>
      {name}: {Number(actual).toLocaleString()}
    </div>
  );
}

function SankeyNode(props: any) {
  const { x, y, width, height, payload } = props;
  const fill = payload?.fill ?? "var(--accent-9)";
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} fillOpacity={1} />
      <text
        x={x + width + 6}
        y={y + height / 2}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={12}
        fill="var(--gray-12)"
      >
        {payload?.name} · {Number(payload?.actual ?? payload?.value ?? 0).toLocaleString()}
      </text>
    </Layer>
  );
}
